import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { queryTestFilterCounts } from '@/features/tests/data/tests';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { buildInFilter, mergeFilters, type GridFilters } from '@/ui/composites/grid/gridFilters';
import type {
  TableDropdownOption,
  TableStatusOption,
} from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { TestFilterCountsDto, TestRunStatus } from '@flowqa/shared';

type CountMap = Partial<Record<string, number>>;

type TestsPageFilterOptionCountArgs = {
  enabled: boolean;
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  search: string;
  selectedStatuses: TestRunStatus[];
  selectedTests: string[];
  selectedSuites: string[];
  selectedTags: string[];
  startDay: string | null;
  endDay: string | null;
  testOptions?: TableDropdownOption[];
  suiteOptions?: TableDropdownOption[];
  statusOptions?: Array<TableStatusOption<TestRunStatus>>;
  tagOptions?: TableDropdownOption[];
};

type CountedTestsPageFilterOptions = {
  countedTestOptions: TableDropdownOption[];
  countedSuiteOptions: TableDropdownOption[];
  countedStatusOptions: Array<TableStatusOption<TestRunStatus>>;
  countedTagOptions: TableDropdownOption[];
};

type TestsPageFilterCountInput = Pick<
  TestsPageFilterOptionCountArgs,
  'selectedStatuses' | 'selectedTests' | 'selectedSuites' | 'selectedTags'
>;

type TestFilterCountGroups = {
  tests: GridFilters;
  suites: GridFilters;
  statuses: GridFilters;
  tags: GridFilters;
};

export const buildTestsPageFilterCountGroups = ({
  selectedStatuses,
  selectedTests,
  selectedSuites,
  selectedTags,
}: TestsPageFilterCountInput): TestFilterCountGroups => ({
  tests: mergeFilters(
    buildInFilter('executionStatus', selectedStatuses),
    buildInFilter('suiteId', selectedSuites),
    buildInFilter('tags', selectedTags),
  ),
  suites: mergeFilters(
    buildInFilter('executionStatus', selectedStatuses),
    buildInFilter('id', selectedTests),
    buildInFilter('tags', selectedTags),
  ),
  statuses: mergeFilters(
    buildInFilter('id', selectedTests),
    buildInFilter('suiteId', selectedSuites),
    buildInFilter('tags', selectedTags),
  ),
  tags: mergeFilters(
    buildInFilter('executionStatus', selectedStatuses),
    buildInFilter('id', selectedTests),
    buildInFilter('suiteId', selectedSuites),
  ),
});

const applyCountsToOptions = (
  options: readonly TableDropdownOption[],
  counts: CountMap | undefined,
): TableDropdownOption[] => {
  if (counts === undefined) {
    return options.map((option) => ({ ...option }));
  }
  return options.map((option) => ({
    ...option,
    count: counts[option.value] ?? 0,
  }));
};

const applyCountsToStatusOptions = (
  options: ReadonlyArray<TableStatusOption<TestRunStatus>>,
  counts: CountMap | undefined,
): Array<TableStatusOption<TestRunStatus>> => {
  if (counts === undefined) {
    return options.map((option) => ({ ...option }));
  }
  const hasErroredOption = options.some((option) => option.value === 'errored');
  return options.map((option) => {
    const groupedErroredCount =
      option.value === 'failed' && !hasErroredOption ? (counts.errored ?? 0) : 0;
    return {
      ...option,
      count: (counts[option.value] ?? 0) + groupedErroredCount,
    };
  });
};

export const useTestsPageFilterOptionCounts = ({
  enabled,
  selectedProjectId,
  selectedOrganisationId,
  search,
  selectedStatuses,
  selectedTests,
  selectedSuites,
  selectedTags,
  startDay,
  endDay,
  testOptions = [],
  suiteOptions = [],
  statusOptions = [],
  tagOptions = [],
}: TestsPageFilterOptionCountArgs): CountedTestsPageFilterOptions => {
  const countGroups = useMemo(
    () =>
      buildTestsPageFilterCountGroups({
        selectedStatuses,
        selectedTests,
        selectedSuites,
        selectedTags,
      }),
    [selectedStatuses, selectedSuites, selectedTags, selectedTests],
  );
  const optionCountKey = useMemo(
    () =>
      JSON.stringify({
        search,
        selectedStatuses,
        selectedTests,
        selectedSuites,
        selectedTags,
        startDay,
        endDay,
      }),
    [endDay, search, selectedStatuses, selectedSuites, selectedTags, selectedTests, startDay],
  );
  const isEnabled = enabled && selectedProjectId !== null;
  const countsQuery = useQuery<TestFilterCountsDto>({
    queryKey: queryKeys.dataGrid(
      gridPaths.tests,
      'filter-option-counts',
      selectedOrganisationId,
      selectedProjectId,
      optionCountKey,
    ),
    enabled: isEnabled,
    queryFn: async (): Promise<TestFilterCountsDto> => {
      if (selectedProjectId === null) {
        return { tests: {}, suites: {}, statuses: {}, tags: {} };
      }
      return await queryTestFilterCounts(selectedProjectId, selectedOrganisationId, {
        search: search.trim() || undefined,
        metricsStartDay: startDay ?? undefined,
        metricsEndDay: endDay ?? undefined,
        filters: countGroups,
      });
    },
    staleTime: 30_000,
  });

  return {
    countedTestOptions: applyCountsToOptions(testOptions, countsQuery.data?.tests),
    countedSuiteOptions: applyCountsToOptions(suiteOptions, countsQuery.data?.suites),
    countedStatusOptions: applyCountsToStatusOptions(statusOptions, countsQuery.data?.statuses),
    countedTagOptions: applyCountsToOptions(tagOptions, countsQuery.data?.tags),
  };
};
